import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useNotification } from "providers/NotificationProvider";
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Input,
  Button,
  UncontrolledTooltip,
} from "reactstrap";
import Avatar from "@mui/material/Avatar";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloseIcon from "@mui/icons-material/Close";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ViewUploadedID from "modals/ViewUploadedID";
import userServices from "services/userServices";
import constants from "constants";
import SupportDetailsCard from "./SupportDetailsCard";
import OrderDetailsCard from "./OrderDetailsCard";
import DisputeDetailsCard from "./DisputeDetailsCard";
import { useAuth } from "context/AuthContext";
import helpers from "utils/helpers";
import { useTranslation } from "context/TranslationProvider";

const {
  CONVERSATION_TYPES,
  CONVERSATION_CHANNELS,
  CONVERSATION_ROLES,
} = helpers;

const ADMIN_MESSAGES_PATH = "/admin/messages";
const ALLOWED_MIME_TYPES = [
  "image/jpeg", "image/png", "image/gif", "image/webp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
];
const MAX_FILE_SIZE_MB = 10;
const MAX_FILES = 5;

const Conversations = () => {
  const { conversationType, conversationId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notify } = useNotification();
  const { user } = useAuth();

  const isModerator =
    user?.role?.slug === "moderator" ||
    user?.role?.slug === "admin" ||
    user?.role?.slug === "super_admin";
  const currentUserId = user?._id?.toString();

  const [messages, setMessages] = useState(
    conversationType === CONVERSATION_TYPES.DISPUTE && isModerator
      ? { sender: [], traveler: [] }
      : []
  );
  const location = useLocation();
  const queryActiveRole = new URLSearchParams(location.search).get("activeRole");
  const [activeRole, setActiveRole] = useState(
    queryActiveRole === "sender" || queryActiveRole === "traveler" ? queryActiveRole : "sender"
  );
  const [newMessage, setNewMessage] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [zoomedImageUrl, setZoomedImageUrl] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showJumpBtn, setShowJumpBtn] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);
  const [disputeDetails, setDisputeDetails] = useState(null);
  const [supportDetails, setSupportDetails] = useState(null);

  const containerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const avatarCacheRef = useRef({});
  const userScrolledUpRef = useRef(false);
  const hideTimerRef = useRef(null);
  const prevLengthRef = useRef(0);
  const notifiedRef = useRef(false);
  const isInitialLoadRef = useRef(true);

  const formatTime = (date) =>
    new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const getInitials = (u) => u?.firstName?.[0]?.toUpperCase() || "?";

  const getCachedAvatarUrl = (u) => {
    if (!u?._id) return;
    if (!avatarCacheRef.current[u._id]) {
      avatarCacheRef.current[u._id] = u.profilePictureUrl
        ? `${constants.s3Bucket}/profile-pics/${u.profilePictureUrl}`
        : null;
    }
    return avatarCacheRef.current[u._id] || undefined;
  };

  const getAttachmentUrl = (att) => {
    if (att._local) return att.url;
    return `${constants.s3Bucket}/message-attachments/${att.fileName}`;
  };

  const handleScroll = () => {
    const el = containerRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 30;
    if (!atBottom) {
      userScrolledUpRef.current = true;
      setShowJumpBtn(true);
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowJumpBtn(false), 3000);
    } else {
      userScrolledUpRef.current = false;
      setShowJumpBtn(false);
    }
  };

  const scrollToBottom = (smooth = true) => {
    const el = containerRef.current;
    if (!el) return;
    userScrolledUpRef.current = false;
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    } else {
      el.scrollTop = el.scrollHeight;
    }
    setShowJumpBtn(false);
  };
  const handleRoleChange = (role) => {
    setActiveRole(role);
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("activeRole", role);
    navigate(`?${searchParams.toString()}`, { replace: true });
  };

  const appendMessage = (msg, role) => {
    if (conversationType === CONVERSATION_TYPES.DISPUTE && isModerator) {
      setMessages((prev) => ({ ...prev, [role]: [...prev[role], msg] }));
    } else {
      setMessages((prev) => [...prev, msg]);
    }
  };

  const updateMessage = (tempId, patch, role) => {
    if (conversationType === CONVERSATION_TYPES.DISPUTE && isModerator) {
      setMessages((prev) => ({
        ...prev,
        [role]: prev[role].map((m) => (m._id === tempId ? { ...m, ...patch } : m)),
      }));
    } else {
      setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, ...patch } : m)));
    }
  };

  const removeMessageById = (id, role) => {
    if (conversationType === CONVERSATION_TYPES.DISPUTE && isModerator) {
      setMessages((prev) => ({ ...prev, [role]: prev[role].filter((m) => m._id !== id) }));
    } else {
      setMessages((prev) => prev.filter((m) => m._id !== id));
    }
  };

  const getSendMeta = () => {
    if (conversationType === CONVERSATION_TYPES.ORDER && orderDetails) {
      const senderId = orderDetails.senderId?._id;
      const travelerId = orderDetails.travelerId?._id;
      const isTraveler = currentUserId === travelerId?.toString();
      return {
        channel: CONVERSATION_CHANNELS.SENDER_TRAVELER,
        role: isTraveler ? CONVERSATION_ROLES.SENDER : CONVERSATION_ROLES.TRAVELER,
        recipientId: isTraveler ? senderId?.toString() : travelerId?.toString(),
      };
    }
    if (conversationType === CONVERSATION_TYPES.SUPPORT_TICKET && supportDetails) {
      return {
        channel: CONVERSATION_CHANNELS.USER_ADMIN,
        role: CONVERSATION_ROLES.SENDER,
        recipientId: supportDetails.userId?._id?.toString(),
      };
    }
    if (conversationType === CONVERSATION_TYPES.DISPUTE && disputeDetails) {
      if (!isModerator) {
        return {
          channel: CONVERSATION_CHANNELS.MODERATOR_SENDER,
          role: CONVERSATION_ROLES.SENDER,
          recipientId: disputeDetails.moderatorId?.toString(),
        };
      }
      const isSender = activeRole === CONVERSATION_ROLES.SENDER;
      return {
        channel: isSender
          ? CONVERSATION_CHANNELS.MODERATOR_SENDER
          : CONVERSATION_CHANNELS.MODERATOR_TRAVELER,
        role: activeRole,
        recipientId: isSender
          ? disputeDetails.orderid.senderId?._id?.toString() || disputeDetails.orderid.senderId?.toString()
          : disputeDetails.orderid.travelerId?._id?.toString() || disputeDetails.orderid.travelerId?.toString(),
      };
    }
    return {};
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    const remaining = MAX_FILES - attachments.length;
    if (remaining <= 0) {
      notify("error", t("page.messages.toast.maxFilesReached", "Maximum 5 files allowed") || `Maximum ${MAX_FILES} files allowed`);
      e.target.value = "";
      return;
    }

    const toAdd = files.slice(0, remaining);
    const valid = [];

    for (const file of toAdd) {
      if (!ALLOWED_MIME_TYPES.includes(file.type)) {
        notify("error", `${file.name}: ${t("page.messages.toast.invalidFileType", "File type not allowed. Please upload an image, PDF, or Office document.")}`);
        continue;
      }
      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        notify("error", `${file.name}: ${t("page.messages.toast.fileTooLarge", "File is too large. Maximum 2MB allowed.")}`);
        continue;
      }
      valid.push({ file, name: file.name, url: URL.createObjectURL(file), type: file.type });
    }

    if (valid.length) setAttachments((prev) => [...prev, ...valid]);
    e.target.value = "";
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => {
      const toRemove = prev[index];

      if (toRemove?._local && toRemove.url) {
        URL.revokeObjectURL(toRemove.url);
      }

      return prev.filter((_, i) => i !== index);
    });
  };

  useEffect(() => {
    return () => {
      attachments.forEach((att) => {
        if (att?._local && att.url) {
          URL.revokeObjectURL(att.url);
        }
      });
    };
  }, [attachments]);

  const buildFormData = (messageText, attachmentList, meta) => {
    const json = {
      conversation_id: conversationId,
      conversation_type: conversationType,
      recipientId: meta.recipientId,
      recipientType: meta.role,
      channel: meta.channel,
    };
    if (messageText) json.message = messageText;
    const fd = helpers.jsonToFormaData(json);
    attachmentList.forEach((att) => {
      if (att?.file) fd.append("messageAttachments", att.file);
    });
    return fd;
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && !attachments.length) {
      return notify("error", t("page.messages.toast.emptyMessage", "Please enter your message"));
    }
    const meta = getSendMeta();
    if (!meta.recipientId || !meta.channel) return;

    const tempId = Date.now().toString();
    if (user?.profilePictureUrl && currentUserId) {
      avatarCacheRef.current[currentUserId] = `${constants.s3Bucket}/profile-pics/${user.profilePictureUrl}`;
    }
    const tempMsg = {
      _id: tempId,
      senderId: {
        _id: currentUserId,
        firstName: "You",
        profilePictureUrl: user?.profilePictureUrl,
      },
      message: newMessage,
      attachments: attachments.map((att) => ({
        originalName: att.name,
        fileType: att.type.startsWith("image/") ? "image" : "file",
        url: att.url,
        _local: true,
      })),
      createdAt: new Date().toISOString(),
      status: "sending",
    };

    scrollToBottom();
    const sentText = newMessage;
    const sentFiles = attachments;
    setNewMessage("");
    setAttachments([]);
    appendMessage(tempMsg, meta.role);

    try {
      const payload = buildFormData(sentText, sentFiles, meta);
      const res = await userServices.createConversationMessage(payload);
      const saved = res?.data?.data || res?.data || res;
      updateMessage(tempId, { _id: saved._id, attachments: saved.attachments || [], status: "sent" }, meta.role);
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.flagged) {
        removeMessageById(tempId, meta.role);
        const { messageKey, reasonType } = err.response.data;
        notify("error", t(messageKey || "page.messages.toast.abuseBlocked"));
        notify("info", t(reasonType === helpers.ABUSE
          ? "page.messages.toast.abuseDetails"
          : "page.messages.toast.chatRulesDetails"
        ));
        return;
      }
      updateMessage(tempId, { status: "failed" }, meta.role);
      notify("error", t("page.messages.toast.sendFailed", "Failed to send message. Click to retry."));
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const retrySendMessage = async (msg, role) => {
    if (msg.attachments?.some((a) => a._local)) {
      notify("error", t("page.messages.toast.retryWithAttachments", "Retry without attachments if they failed"));
      return;
    }

    const meta = getSendMeta();
    if (!meta.recipientId || !meta.channel) return;
    updateMessage(msg._id, { status: "sending" }, role);
    try {
      const payload = buildFormData(msg.message, [], meta);
      const res = await userServices.createConversationMessage(payload);
      const saved = res?.data?.data || res?.data || res;
      updateMessage(msg._id, { _id: saved._id, status: "sent" }, role);
    } catch (err) {
      if (err?.response?.status === 422 && err?.response?.data?.flagged) {
        removeMessageById(msg._id, role);
        const { messageKey, reasonType } = err.response.data;
        notify("error", t(messageKey || "page.messages.toast.abuseBlocked"));
        notify("info", t(reasonType === helpers.ABUSE
          ? "page.messages.toast.abuseDetails"
          : "page.messages.toast.chatRulesDetails"
        ));
        return;
      }
      updateMessage(msg._id, { status: "failed" }, role);
      notify("error", t("page.messages.toast.sendFailed", "Failed to send message. Click to retry."));
    }
  };

  const openImageZoom = (url) => {
    setZoomedImageUrl(url);
    setImageModalOpen(true);
  };

  const closeImageZoom = () => setImageModalOpen(false);

  useEffect(() => {
    if (!conversationId || !conversationType) return;
    setLoading(true);
    const fetchConversationData = async () => {
      try {
        const convRes = await userServices.fetchOrderConversation({
          conversation_id: conversationId,
          conversation_type: conversationType,
        });
        const dataObj = convRes?.data || convRes;
        const threads = dataObj?.directMessage || [];

        if (conversationType === CONVERSATION_TYPES.DISPUTE && isModerator) {
          const grouped = { sender: [], traveler: [] };
          threads.forEach((thread) => {
            if (thread.channel === CONVERSATION_CHANNELS.MODERATOR_SENDER)
              grouped.sender.push(...(thread.messages || []));
            if (thread.channel === CONVERSATION_CHANNELS.MODERATOR_TRAVELER)
              grouped.traveler.push(...(thread.messages || []));
          });
          setMessages(grouped);
        } else {
          setMessages(threads.flatMap((thread) => thread.messages || []));
        }

        if (conversationType === CONVERSATION_TYPES.ORDER) {
          const res = await userServices.fetchOrderDetails(conversationId);
          const orderObj = res?.data || res;
          setOrderDetails(orderObj);
        }
        if (conversationType === CONVERSATION_TYPES.SUPPORT_TICKET) {
          try {
            const res = await userServices.fetchSupportRequestDetails(conversationId);
            const ticket = res?.data || res;

            if (!ticket) {
              throw new Error("page.messages.support.notFound");
            }

            if (
              ticket.assignedTo?._id !== user?._id &&
              user?.role?.slug === "moderator"
            ) {
              throw new Error("page.messages.support.notAssigned");
            }

            setSupportDetails(ticket);
          } catch (err) {
              if (err.response?.status === 404) {
                if (!notifiedRef.current) {
                  notifiedRef.current = true;
                  notify(
                    "error",
                    t("page.messages.support.notFound", "This support ticket no longer exists.")
                  );
                  navigate(ADMIN_MESSAGES_PATH);
                }
                return;
              }

              throw err;
            }
        }

          if (conversationType === CONVERSATION_TYPES.DISPUTE) {
            try {
              const res = await userServices.fetchDisputeDetails(conversationId);
              const dispute = res?.data || res?.dispute || res;
              if (dispute?.error) {
                const errorMsg = dispute?.error;
                if (!notifiedRef.current) {
                  notifiedRef.current = true;
                  notify("error", errorMsg);
                  navigate(ADMIN_MESSAGES_PATH);
                }
                return;
              }
              const isAssignedToMe = (dispute?.dispute?.assignedTo?._id || dispute?.assignedTo?._id) === user?._id;
              if (!isAssignedToMe && user.role.slug === "moderator") {
                throw new Error("page.messages.dispute.notAssigned");
              }
              setDisputeDetails(dispute.dispute || dispute);
            } catch (err) {
                if (err.response?.status === 404) {
                  // Dispute was deleted
                  if (!notifiedRef.current) {
                    notifiedRef.current = true;
                    notify("error", t("page.messages.dispute.notFound", "This dispute no longer exists."));
                    navigate(ADMIN_MESSAGES_PATH);
                  }
                  return; 
                }
                throw err;
              }
          }
  } catch (err) {
    if (!notifiedRef.current) {
      notifiedRef.current = true;
      notify("error", t(err.message) || "Cannot access this conversation.");
      navigate(ADMIN_MESSAGES_PATH);
    }
  } finally {
    setLoading(false);
  }
};

    fetchConversationData();
  }, [conversationId, conversationType, isModerator, user, navigate, notify, t]);

  const markAsRead = async () => {
    if (loading || !conversationId || !conversationType) return;

    let channelToMark = null;
    if (conversationType === CONVERSATION_TYPES.DISPUTE && isModerator) {
      channelToMark =
        activeRole === "sender"
          ? CONVERSATION_CHANNELS.MODERATOR_SENDER
          : CONVERSATION_CHANNELS.MODERATOR_TRAVELER;
    } else {
      if (conversationType === CONVERSATION_TYPES.ORDER) {
        channelToMark = CONVERSATION_CHANNELS.SENDER_TRAVELER;
      } else if (conversationType === CONVERSATION_TYPES.SUPPORT_TICKET) {
        channelToMark = CONVERSATION_CHANNELS.USER_ADMIN;
      }
    }

    if (!channelToMark) return;

    try {
      await userServices.updateConversationMessage({
        type: "markAllAsRead",
        conversationId,
        conversationType,
        channel: channelToMark,
      });
      window.dispatchEvent(new Event("messagesRead"));
    } catch (error) {
      console.error("Error marking conversation as read inside chat:", error);
    }
  };

  const activeMessagesLength = (
    conversationType === CONVERSATION_TYPES.DISPUTE && isModerator
      ? messages[activeRole]?.length
      : messages?.length
  ) || 0;

  useEffect(() => {
    if (!loading) {
      markAsRead();
    }
    // eslint-disable-next-line
  }, [activeRole, loading, conversationId, conversationType, activeMessagesLength]);

  useEffect(() => {
    if (
      conversationType === CONVERSATION_TYPES.DISPUTE &&
      isModerator &&
      disputeDetails?.orderid
    ) {
      const searchParams = new URLSearchParams(location.search);
      const urlActiveRole = searchParams.get("activeRole");
      if (urlActiveRole === "sender" || urlActiveRole === "traveler") {
        setActiveRole(urlActiveRole);
      } else {
        const creatorId = (disputeDetails.userId?._id || disputeDetails.userId)?.toString();
        const travelerId = (disputeDetails.orderid?.travelerId?._id || disputeDetails.orderid?.travelerId)?.toString();

        if (creatorId && travelerId && creatorId === travelerId) {
          setActiveRole("traveler");
        } else {
          setActiveRole("sender");
        }
      }
    }
  }, [conversationType, isModerator, disputeDetails, location.search]);

  useEffect(() => {
    isInitialLoadRef.current = true;
    prevLengthRef.current = 0;
  }, [conversationId, activeRole]);

  useEffect(() => {
    let t1, t2, t3;

    if (!loading) {
      const list =
        conversationType === CONVERSATION_TYPES.DISPUTE && isModerator
          ? messages[activeRole] || []
          : messages;

      if (isInitialLoadRef.current) {
        isInitialLoadRef.current = false;
        scrollToBottom(false);
        t1 = setTimeout(() => scrollToBottom(false), 50);
        t2 = setTimeout(() => scrollToBottom(false), 150);
        t3 = setTimeout(() => scrollToBottom(false), 300);
      } else if (list.length > prevLengthRef.current && !userScrolledUpRef.current) {
        scrollToBottom();
      }
      prevLengthRef.current = list.length;
    }

    return () => {
      if (t1) clearTimeout(t1);
      if (t2) clearTimeout(t2);
      if (t3) clearTimeout(t3);
    };
  }, [messages, activeRole, loading, conversationType, isModerator]);

  const conversationTitleMap = {
    order: `${t("page.messages.orderConversationTitle", "Conversations For Order")} : ${orderDetails?.orderid || ""}`,
    dispute: `${t("page.messages.disputeConversationTitle", "Conversations For Dispute")} : ${disputeDetails?.title || ""}`,
    "support-ticket": `${t("page.messages.supportConversationTitle", "Conversations For Support Ticket")} : ${supportDetails?.title || ""}`,
  };

  const getDisputeCreatorRole = () => {
    if (!disputeDetails?.orderid || !disputeDetails?.userId) return null;
    const creatorId = disputeDetails.userId?.toString();
    const senderId =
      disputeDetails.orderid.senderId?._id?.toString() ||
      disputeDetails.orderid.senderId?.toString();
    const travelerId =
      disputeDetails.orderid.travelerId?._id?.toString() ||
      disputeDetails.orderid.travelerId?.toString();
    if (creatorId === senderId) return "sender";
    if (creatorId === travelerId) return "traveler";
    return null;
  };

  const getMessagesToDisplay = () => {
    if (conversationType === "dispute" && isModerator) {
      return messages?.[activeRole] || [];
    }
    return Array.isArray(messages) ? messages : [];
  };

  const renderMessageAttachments = (atts = []) => {
    if (!atts?.length) return null;
    return (
      <div className="mt-1">
        {atts.map((att, i) => {
          const url = getAttachmentUrl(att);
          if (att.fileType === "image") {
            return (
              <img
                key={i}
                src={url}
                alt={att.originalName}
                className="msg-attachment-image border rounded cursor-pointer d-block mt-1"
                onClick={() => openImageZoom(url)}
                onLoad={() => {
                  if (!userScrolledUpRef.current) {
                    scrollToBottom(false);
                  }
                }}
              />
            );
          }
          return (
            <a
              key={i}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="msg-file-link d-flex align-items-center mt-1"
            >
              <InsertDriveFileIcon className="msg-file-icon mr-1" />
              <span className="text-truncate msg-file-name">{att.originalName}</span>
            </a>
          );
        })}
      </div>
    );
  };

  const renderMessageStatus = (msg, role) => {
    if (msg.status === "sending") return t("page.messages.sending", "Sending...");
    if (msg.status === "failed") {
      return (
        <>
          <span className="text-danger mr-2">{t("page.messages.failed", "Failed to send")}</span>
          <Button size="sm" color="link" className="p-0" onClick={() => retrySendMessage(msg, role)}>
            {t("page.messages.retry", "Retry")}
          </Button>
        </>
      );
    }
    return formatTime(msg.createdAt);
  };

  const renderMessages = (msgs = []) => {
    if (!Array.isArray(msgs) || msgs.length === 0) return null;
    return msgs.map((msg) => {
      const sender = msg.senderId || {};
      const isMe = sender?._id?.toString() === currentUserId;
      return (
        <div key={msg._id} className={`message-row ${isMe ? "me" : "other"}`}>
          {!isMe && (
            <Avatar
              src={getCachedAvatarUrl(sender)}
              className="mx-2 small-avatar"
              imgProps={{
                onLoad: () => {
                  if (!userScrolledUpRef.current) {
                    scrollToBottom(false);
                  }
                }
              }}
            >
              {getInitials(sender)}
            </Avatar>
          )}
          <div className="message-content">
            <div className={`message-bubble ${isMe ? "bubble-me" : "bubble-other"}`}>
              {msg.message && <span>{msg.message}</span>}
              {renderMessageAttachments(msg.attachments)}
            </div>
            <div className="message-meta">
              <small className="message-sender mr-2">
                {isMe ? t("page.messages.you", "You") : sender.firstName || t("page.messages.support.na", "N/A")}
              </small>
              <small className="message-time">
                {renderMessageStatus(msg, activeRole)}
              </small>
            </div>
          </div>
          {isMe && (
            <Avatar
              src={getCachedAvatarUrl(sender)}
              className="mx-2 small-avatar"
              imgProps={{
                onLoad: () => {
                  if (!userScrolledUpRef.current) {
                    scrollToBottom(false);
                  }
                }
              }}
            >
              {getInitials({ firstName: "You" })}
            </Avatar>
          )}
        </div>
      );
    });
  };

  const disputeCreatorRole = getDisputeCreatorRole();
  const messagesToDisplay = getMessagesToDisplay();

  return (
    <>
      <Container fluid className="mt-4">
        <Row>
          <Col lg="8">
            <Card className="shadow-lg">
              <CardHeader>
                <Row className="align-items-center">
                  <Col xs="8">
                    <h3 className="mb-0">
                      {conversationTitleMap[conversationType] || "Conversation"}
                    </h3>
                    {conversationType === "dispute" && isModerator && disputeDetails && (
                      <div className="d-flex mt-2">
                        <Button
                          id="dispute-sender-chat-btn"
                          size="sm"
                          color={activeRole === "sender" ? "primary" : "secondary"}
                          className="mr-2"
                          onClick={() => handleRoleChange("sender")}
                        >
                          {t("page.messages.sender", "Sender")}
                        </Button>
                        {disputeCreatorRole === "sender" && (
                          <UncontrolledTooltip placement="top" target="dispute-sender-chat-btn">
                            {t("page.messages.disputeCreator", "Dispute Creator")}
                          </UncontrolledTooltip>
                        )}
                        <Button
                          id="dispute-traveler-chat-btn"
                          size="sm"
                          color={activeRole === "traveler" ? "primary" : "secondary"}
                          onClick={() => handleRoleChange("traveler")}
                        >
                          {t("page.messages.traveler", "Traveler")}
                        </Button>
                        {disputeCreatorRole === "traveler" && (
                          <UncontrolledTooltip placement="top" target="dispute-traveler-chat-btn">
                            {t("page.messages.disputeCreator", "Dispute Creator")}
                          </UncontrolledTooltip>
                        )}
                      </div>
                    )}
                  </Col>
                  <Col xs="4" className="text-right">
                    <Button color="secondary" onClick={() => navigate(-1)}>
                      {t("page.messages.back", "Back")}
                    </Button>
                  </Col>
                </Row>
              </CardHeader>
              <CardBody className="p-0">
                <div className="conversation-body" ref={containerRef} onScroll={handleScroll}>
                  {loading ? (
                    <p className="text-center text-muted mt-3">{t("common.loading", "Loading....")}</p>
                  ) : (
                    renderMessages(messagesToDisplay)
                  )}
                  <div ref={messagesEndRef} />
                  {showJumpBtn && (
                    <button className="jump-to-bottom" onClick={scrollToBottom}>
                      <ArrowDownwardIcon className="mr-2" />
                      {t("page.messages.jumpToBottom", "Jump to bottom")}
                    </button>
                  )}
                </div>
              </CardBody>
              <CardFooter className="conversation-footer-pad">
                {attachments.length > 0 && (
                  <div className="d-flex flex-wrap msg-attachment-chips mb-2">
                    {attachments.map((att, i) => (
                      <div key={i} className="msg-attachment-chip d-flex align-items-center">
                        {att.type.startsWith("image/") ? (
                          <img src={att.url} alt="" className="msg-chip-thumb mr-1" />
                        ) : (
                          <InsertDriveFileIcon className="msg-chip-file-icon mr-1" />
                        )}
                        <span className="text-truncate msg-chip-name">{att.name}</span>
                        <button
                          className="btn btn-link p-0 ml-1 msg-chip-remove"
                          onClick={() => removeAttachment(i)}
                        >
                          <CloseIcon className="msg-chip-close-icon" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="d-flex align-items-end msg-composer">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                    multiple
                    className="d-none"
                    onChange={handleFileSelect}
                  />
                  <button
                    id="attach-file-btn"
                    className="btn btn-outline-secondary btn-sm msg-attach-btn"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= MAX_FILES}
                  >
                    <AttachFileIcon className="msg-attach-icon" />
                  </button>
                  <UncontrolledTooltip placement="top" target="attach-file-btn">
                    {attachments.length >= MAX_FILES
                      ? `Max ${MAX_FILES} files`
                      : t("page.messages.attachFile", "Attach file")}
                  </UncontrolledTooltip>
                  <div className="msg-textarea-wrap">
                    <Input
                      type="textarea"
                      rows="1"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder={t("page.messages.placeholder", "Write a message...")}
                      className="msg-textarea"
                    />
                  </div>
                  <Button
                    color="primary"
                    className="msg-send-btn"
                    disabled={!newMessage.trim() && !attachments.length}
                    onClick={handleSendMessage}
                  >
                    {t("page.messages.send", "Send")}
                  </Button>
                </div>
              </CardFooter>
            </Card>
          </Col>
          <Col lg="4">
            {conversationType === CONVERSATION_TYPES.ORDER && (
              <OrderDetailsCard order={orderDetails} />
            )}
            {conversationType === CONVERSATION_TYPES.DISPUTE && (
              <DisputeDetailsCard dispute={disputeDetails} />
            )}
            {conversationType === CONVERSATION_TYPES.SUPPORT_TICKET && (
              <SupportDetailsCard ticket={supportDetails} />
            )}
          </Col>
        </Row>
      </Container>
      <ViewUploadedID
        idUrl={zoomedImageUrl}
        isOpen={imageModalOpen}
        toggle={closeImageZoom}
      />
    </>
  );
};

export default Conversations;
